import { getInspirationDetail } from '@/api/inspiration'
import {
  isInspirationApiSuccess,
  mergeInspirationDetailCard,
  parseInspirationParamInfo,
} from '@/utils/inspirationMappers'

export const INSPIRATION_APPLY_STORAGE_KEY = 'de-inspiration-apply-v1'

const META_KEYS = new Set([
  'router_url',
  'routerUrl',
  'subtype',
  'type',
  'scene',
  'scenes',
])

const VIDEO_MODES = ['general', 'keyframe', 'effect']
const ECOM_IMAGE_TYPES = ['set', 'detail']

export function mapInspirationSubtypeToVideoMode(subtype) {
  const idx = Number(subtype)
  if (!Number.isFinite(idx) || idx < 0) return VIDEO_MODES[0]
  return VIDEO_MODES[idx] || VIDEO_MODES[VIDEO_MODES.length - 1]
}

export function mapInspirationSubtypeToEcomImageType(subtype) {
  const idx = Number(subtype)
  if (!Number.isFinite(idx) || idx < 0) return ECOM_IMAGE_TYPES[0]
  return ECOM_IMAGE_TYPES[idx] || ECOM_IMAGE_TYPES[ECOM_IMAGE_TYPES.length - 1]
}

export function buildWorkspaceParamsFromParamInfo(rawParamInfo) {
  const paramInfo = parseInspirationParamInfo(rawParamInfo)
  const params = {}
  Object.keys(paramInfo).forEach((key) => {
    if (META_KEYS.has(key)) return
    const value = paramInfo[key]
    if (value === undefined || value === null) return
    params[key] = value
  })
  return params
}

export function stashInspirationApplyPayload(payload) {
  if (!payload) return
  try {
    sessionStorage.setItem(INSPIRATION_APPLY_STORAGE_KEY, JSON.stringify(payload))
  } catch (_) {
    /* ignore quota errors */
  }
}

export function consumeInspirationApplyPayload() {
  try {
    const raw = sessionStorage.getItem(INSPIRATION_APPLY_STORAGE_KEY)
    if (!raw) return null
    sessionStorage.removeItem(INSPIRATION_APPLY_STORAGE_KEY)
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (_) {
    sessionStorage.removeItem(INSPIRATION_APPLY_STORAGE_KEY)
    return null
  }
}

/**
 * 容错修正智能体 Hub 路径：/digital-employee/:empId/xxx（empId 为数字）
 * 自动补上缺失的 /use 段（真实路由为 /digital-employee/use/:empId/xxx）。
 * 仅处理数字 empId 段，不影响 ecom-workflow / quick-ecom 等真实顶层路由。
 */
function normalizeAgentHubPath(path) {
  if (/^\/digital-employee\/\d+(\/|$)/.test(path)) {
    return path.replace(/^\/digital-employee\//, '/digital-employee/use/')
  }
  return path
}

export function resolveInspirationRouterTarget(routerUrl, empId = '2') {
  const raw = String(routerUrl || '').trim()
  if (!raw) return null

  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw)
      const query = {}
      url.searchParams.forEach((value, key) => {
        query[key] = value
      })
      return { path: normalizeAgentHubPath(url.pathname), query }
    } catch (_) {
      return null
    }
  }

  if (!raw.includes('/')) {
    return { name: raw, params: { empId: String(empId) } }
  }

  let path = raw.startsWith('/') ? raw : `/${raw}`
  path = path.replace(':empId', String(empId))
  return { path: normalizeAgentHubPath(path) }
}

export async function fetchInspirationDetailCard(card, tabId = '') {
  if (!card || card.id == null) {
    throw new Error('灵感数据无效')
  }
  const res = await getInspirationDetail({ id: card.id })
  if (!isInspirationApiSuccess(res) || !res.data) {
    throw new Error('获取灵感详情失败')
  }
  return mergeInspirationDetailCard(card, res.data, tabId || card.tabId || '')
}

export function buildInspirationApplyPayload(detailCard) {
  const paramInfo = parseInspirationParamInfo(detailCard?.paramInfo)
  const routerUrl = String(paramInfo.router_url || paramInfo.routerUrl || '').trim()
  if (!routerUrl) {
    throw new Error('该灵感暂未配置跳转链接')
  }

  const type = String(paramInfo.type || detailCard?.type || '').trim().toLowerCase()
  const subtype = Number(paramInfo.subtype)
  const normalizedSubtype = Number.isFinite(subtype) ? subtype : 0
  const workspaceParams = buildWorkspaceParamsFromParamInfo(paramInfo)

  if (type === 'video') {
    workspaceParams.creativeMode = mapInspirationSubtypeToVideoMode(normalizedSubtype)
  } else if (type === 'image' || type === 'ecom') {
    workspaceParams.imageType = mapInspirationSubtypeToEcomImageType(normalizedSubtype)
  }

  return {
    inspirationId: String(detailCard?.id || ''),
    title: String(detailCard?.title || '').trim(),
    type,
    subtype: normalizedSubtype,
    routerUrl,
    params: workspaceParams,
  }
}

export async function navigateInspirationSameStyle(router, card, options = {}) {
  const {
    tabId = '',
    fetchDetail = false,
    empId = '2',
  } = options

  let detailCard = card
  const cachedParamInfo = parseInspirationParamInfo(card?.paramInfo)
  const hasRouterUrl = !!(cachedParamInfo.router_url || cachedParamInfo.routerUrl)

  if (fetchDetail || !hasRouterUrl) {
    detailCard = await fetchInspirationDetailCard(card, tabId)
  }

  const payload = buildInspirationApplyPayload(detailCard)
  stashInspirationApplyPayload(payload)

  const target = resolveInspirationRouterTarget(payload.routerUrl, empId)
  if (!target) {
    throw new Error('跳转链接无效')
  }

  const query = {
    ...(target.query || {}),
    insp: '1',
    subtype: String(payload.subtype),
    ref: payload.inspirationId,
  }

  await router.push({
    ...target,
    query,
  }).catch(() => {})

  return detailCard
}

export function shouldApplyInspirationFromRoute(route) {
  return String(route?.query?.insp || '') === '1'
}

export function clearInspirationRouteQuery(router, route) {
  if (!router || !route) return
  const query = { ...(route.query || {}) }
  delete query.insp
  delete query.subtype
  delete query.ref
  router.replace({
    path: route.path,
    query,
    hash: route.hash,
  }).catch(() => {})
}
